import React, { useMemo } from "react"
import DOMPurify from "dompurify"
import { useGetAllAppCopyQuery } from "../../core/api/experience-api"

// Reads a single app-copy row from the cached bulk dictionary.
// All AppCopy components share one /copy network request thanks to RTK Query
// dedupe + selectFromResult.
//
// Returns: { row, isLoading } — `row` is { key, title, body, ... } or undefined.
export function useAppCopy(appKey) {
    return useGetAllAppCopyQuery(undefined, {
        selectFromResult: ({ data, isLoading }) => ({
            row: (data || []).find(r => r.key === appKey),
            isLoading,
        }),
    })
}

// Tiny render component — gives you just the requested field, no styling.
//
//   <AppCopy appKey="experience.header" field="title" />
//   <AppCopy appKey="experience.header" field="description" />
//
// `field` accepts:
//   - "title" → plain string (rendered as text)
//   - "description" / "body" → sanitized HTML (rendered via dangerouslySetInnerHTML)
//
// For HTML output, the wrapping element defaults to <span>; override via
// `component` prop if you need a block container. Use the children render-prop
// for full control: <AppCopy ...>{(value) => <Typography>{value}</Typography>}</AppCopy>.
//
// Returns null if the row or the requested field is empty.
export default function AppCopy({ appKey, field = "title", component: Component = "span", children, ...rest }) {
    const { row } = useAppCopy(appKey)
    const isHtmlField = field === "body" || field === "description"
    const raw = isHtmlField ? row?.body : row?.title

    // Memoised sanitization — must be declared unconditionally to obey React's
    // rules of hooks (no early returns above this).
    const sanitized = useMemo(() => {
        if (!isHtmlField || !raw) return ""
        return DOMPurify.sanitize(raw, {
            ALLOWED_TAGS: [
                "p", "br", "h1", "h2", "h3", "h4", "h5", "h6", "a",
                "strong", "b", "em", "i", "u", "ul", "ol", "li", "span", "div",
                // Tables
                "table", "thead", "tbody", "tfoot", "tr", "td", "th", "caption", "colgroup", "col",
            ],
            ALLOWED_ATTR: [
                "href", "target", "rel",
                "class", "style", "id",
                "colspan", "rowspan", "align", "valign", "width", "height",
            ],
            // ALLOW_DATA_ATTR is on by default but spelling it out makes it
            // clear we accept arbitrary data-* attributes (data-colwidth,
            // data-renderer-mark, data-text-custom-color, data-align, …).
            ALLOW_DATA_ATTR: true,
            ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|#|\/)/i,
            ADD_ATTR: ["target"],
        })
    }, [raw, isHtmlField])

    if (!row || !raw) return null

    if (children) {
        // Render-prop mode — caller decides the markup completely.
        return children(isHtmlField ? sanitized : raw, row)
    }

    if (isHtmlField) {
        return <Component {...rest} dangerouslySetInnerHTML={{ __html: sanitized }} />
    }

    return <Component {...rest}>{raw}</Component>
}
